import React, { useRef, useEffect } from "react";
import * as THREE from "three";

/* Robot IA 3D "LIBI" (three.js) */
export default function Robot3D() {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const W = mount.clientWidth, H = mount.clientHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, W / H, 0.1, 100);
    camera.position.set(0, 0.4, 7.2);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.setSize(W, H);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0x554433, 0.7));
    const key = new THREE.DirectionalLight(0xffe9b8, 1.4); key.position.set(4, 6, 5); scene.add(key);
    const rim = new THREE.DirectionalLight(0x7c5cff, 1.1); rim.position.set(-5, 2, -4); scene.add(rim);
    const fill = new THREE.PointLight(0x2f6bff, 0.9, 20); fill.position.set(0, -2, 4); scene.add(fill);

    const goldMat = new THREE.MeshStandardMaterial({ color: 0x3b82f6, metalness: 0.95, roughness: 0.25 });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x14141e, metalness: 0.8, roughness: 0.35 });
    const glowMat = new THREE.MeshStandardMaterial({ color: 0x7c5cff, emissive: 0x7c5cff, emissiveIntensity: 2.2, metalness: 0.2, roughness: 0.4 });
    const goldGlow = new THREE.MeshStandardMaterial({ color: 0x2f6bff, emissive: 0x2f6bff, emissiveIntensity: 1.4, metalness: 0.3, roughness: 0.4 });

    const robot = new THREE.Group();

    const head = new THREE.Mesh(new THREE.SphereGeometry(1.05, 48, 48), darkMat);
    head.scale.set(1, 0.92, 0.95); head.position.y = 1.55; robot.add(head);
    const visor = new THREE.Mesh(new THREE.SphereGeometry(0.98, 48, 48, 0, Math.PI * 2, 0.9, 1.1), goldMat);
    visor.position.y = 1.55; visor.scale.set(1.02, 0.94, 0.97); robot.add(visor);
    const eyeGeo = new THREE.SphereGeometry(0.13, 24, 24);
    const eyeL = new THREE.Mesh(eyeGeo, glowMat); eyeL.position.set(-0.33, 1.62, 0.86);
    const eyeR = new THREE.Mesh(eyeGeo, glowMat); eyeR.position.set(0.33, 1.62, 0.86);
    robot.add(eyeL, eyeR);
    const antStem = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.55, 16), goldMat);
    antStem.position.y = 2.75; robot.add(antStem);
    const antTip = new THREE.Mesh(new THREE.SphereGeometry(0.11, 20, 20), goldGlow);
    antTip.position.y = 3.05; robot.add(antTip);
    const earGeo = new THREE.CylinderGeometry(0.16, 0.16, 0.16, 24);
    const earL = new THREE.Mesh(earGeo, goldMat); earL.rotation.z = Math.PI / 2; earL.position.set(-1.06, 1.55, 0);
    const earR = earL.clone(); earR.position.x = 1.06; robot.add(earL, earR);

    const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.95, 1.7, 32), darkMat);
    torso.position.y = -0.15; robot.add(torso);
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.26, 28, 28), goldGlow);
    core.position.set(0, 0.15, 0.78); robot.add(core);
    const coreRing = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.045, 16, 48), goldMat);
    coreRing.position.set(0, 0.15, 0.78); robot.add(coreRing);
    const hip = new THREE.Mesh(new THREE.SphereGeometry(0.55, 28, 28), goldMat);
    hip.scale.set(1, 0.5, 0.9); hip.position.y = -1.15; robot.add(hip);

    const armGeo = new THREE.SphereGeometry(0.3, 24, 24);
    const armL = new THREE.Mesh(armGeo, goldMat); armL.scale.set(0.7, 1.5, 0.7); armL.position.set(-1.45, 0, 0);
    const armR = armL.clone(); armR.position.x = 1.45;
    const handGeo = new THREE.SphereGeometry(0.2, 20, 20);
    const handL = new THREE.Mesh(handGeo, darkMat); handL.position.set(-1.45, -0.85, 0);
    const handR = handL.clone(); handR.position.x = 1.45;
    robot.add(armL, armR, handL, handR);

    const orbit = new THREE.Group();
    const ring = new THREE.Mesh(new THREE.TorusGeometry(2.15, 0.02, 12, 90), goldMat);
    ring.rotation.x = Math.PI / 2.4; orbit.add(ring);
    const sats = [];
    for (let i = 0; i < 3; i++) {
      const s = new THREE.Mesh(new THREE.SphereGeometry(0.09, 16, 16), i === 1 ? glowMat : goldGlow);
      orbit.add(s); sats.push(s);
    }
    robot.add(orbit);
    scene.add(robot);

    const isMobile = window.innerWidth < 768;
    const pGeo = new THREE.BufferGeometry();
    const pCount = isMobile ? 45 : 120, pos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i++) pos[i] = (Math.random() - 0.5) * 10;
    pGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const pts = new THREE.Points(pGeo, new THREE.PointsMaterial({ color: 0x2f6bff, size: 0.03, transparent: true, opacity: 0.7 }));
    scene.add(pts);

    let mx = 0, my = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener("pointermove", onMove);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      const t = clock.getElapsedTime();
      if (!reduced) {
        robot.position.y = Math.sin(t * 1.1) * 0.18;
        robot.rotation.y += (mx * 0.5 - robot.rotation.y) * 0.05;
        robot.rotation.x += (my * 0.18 - robot.rotation.x) * 0.05;
        armL.position.y = Math.sin(t * 1.4) * 0.12;
        armR.position.y = Math.sin(t * 1.4 + Math.PI) * 0.12;
        handL.position.y = -0.85 + Math.sin(t * 1.4) * 0.12;
        handR.position.y = -0.85 + Math.sin(t * 1.4 + Math.PI) * 0.12;
        orbit.rotation.y = t * 0.5;
        sats.forEach((s, i) => {
          const a = t * 0.9 + (i * Math.PI * 2) / 3;
          s.position.set(Math.cos(a) * 2.15, Math.sin(a) * 0.55, Math.sin(a) * 2.15 * 0.4);
        });
        antTip.material.emissiveIntensity = 1.2 + Math.sin(t * 3) * 0.6;
        core.material.emissiveIntensity = 1.2 + Math.sin(t * 2.2) * 0.5;
        pts.rotation.y = t * 0.03;
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onMove);
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);
  return (
    <div className="robot-wrap rise d2" ref={mountRef} data-testid="robot-3d">
      <span className="robot-tag">LIBI · Tu mentor IA 24/7</span>
    </div>
  );
}
